import React, { useEffect, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import UserContext from "../../components/User_Context.js";
import * as menu from "../../assets/menus/menu.js";
import * as breadcrumb from "../../components/ui/breadcrumb.js";
import * as api from "../../api/apiConnector.js";
import { useTranslation } from "react-i18next";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import { LisLabels } from "../_label/lis_labels/lis_labels.js";
import Choices from "../../components/Choices.js";
import LisTableHeader from "../../components/lis/LisTableHeader.js";
import LisMainTable from "../../components/lis/LisMainTable.js";
import Select from "react-select";

const LisAlertAssignmentScreen = () => {
  const { currentUser } = useContext(UserContext);

  const navigate = useNavigate();
  const { t } = useTranslation();
  const MySwal = withReactContent(Swal);

  const i18n = menu.getI18n();
  i18n.addResourceBundle("it", "lisLabels", LisLabels.it, true, true);
  i18n.addResourceBundle("en", "lisLabels", LisLabels.en, true, true);

  /**
   * Get deadline urgency styling based on days since test
   * No background colors or borders, just text indicator
   */
  const getDeadlineStyle = (days) => {
    // No special styling, keep column uniform
    return {};
  };

  /**
   * Get deadline icon based on days since test
   */
  const getDeadlineIcon = (days) => {
    if (days === null || days === undefined) return '📅';
    
    if (days > 7) return '🔴';
    if (days > 3) return '⚠️';
    return '✅';
  };

  const [tableData, setTableData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [assignmentStatus, setAssignmentStatus] = useState("ALL"); // Default: show all
  const [activeFilters, setActiveFilters] = useState({
    fromDate: "",
    toDate: "",
    testCode: "",
    materialName: "",
    microorganismName: "",
    requestId: "",
  });

  const [filterInputs, setFilterInputs] = useState({
    fromDate: "",
    toDate: "",
    testCode: "",
    materialName: "",
    microorganismName: "",
    requestId: "",
  });

  // Admin visibility and BU filter
  const [isAdmin, setIsAdmin] = useState(false);
  const [allBusinessUnits, setAllBusinessUnits] = useState([]);
  const [selectedBUFilter, setSelectedBUFilter] = useState("");
  const [noRightsError, setNoRightsError] = useState(null);

  // Modal states for assignment
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [selectedAlertType, setSelectedAlertType] = useState(null); // 'ICA' | 'ALERT'
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [isAssigning, setIsAssigning] = useState(false);
  
  // Counters for assigned/unassigned alerts
  const [counters, setCounters] = useState({
    toBeAssigned: 0,
    assigned: 0,
    total: 0
  });
  
  // Side panel for alert details
  const [showDetailsPanel, setShowDetailsPanel] = useState(false);
  const [selectedAlertDetails, setSelectedAlertDetails] = useState(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  
  // Collapsible sections state (true = open, false = closed)
  const [openSections, setOpenSections] = useState({
    patient: true,
    microorganisms: true,
    assignment: true,
  });

  // Per-microorganism collapse state (false = expanded)
  const [collapsedMicroorganisms, setCollapsedMicroorganisms] = useState({});
  
  // Toggle section open/close
  const toggleSection = (section) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  
  // Uniform sizing consistent with DMS table toolbar
  const toolbarItemWidthPx = 130;
  const compactHeightPx = 42;
  const uniformControlStyle = {
    height: compactHeightPx,
    minHeight: compactHeightPx,
  };

  const assignmentStatusOptions = [
    { value: "TO_BE_ASSIGNED", label: "🔴 Da Assegnare" },
    { value: "ASSIGNED", label: "✅ Assegnati" },
    { value: "ALL", label: "📋 Tutti" },
  ];

  useEffect(() => {
    // Set breadcrumb
    const path = [
      { link: "/dashboard", name: "Home" },
      { link: "/admin", name: "Admin" },
      { link: "/admin/lis_alert_assignment", name: "LIS Alert Assignment" },
    ];
    breadcrumb.set(path);

    fetchTableData({ page: 1, pageSizeParam: 100, ...activeFilters });

    // Write menu to make it visible
    menu.write({ user: currentUser() });
  }, []);

  const fetchBusinessUnits = async () => {
    try {
      const response = await api.sendMessage("admin/lis/infectionAlerts/businessUnits", {
        user: currentUser(),
        body: {},
      });
      if (response.success) {
        setAllBusinessUnits(response.businessUnits || []);
      }
    } catch (error) {
      console.error("Error fetching business units:", error);
    }
  };

  const fetchTableData = async (params = {}) => {
    const {
      page = 1,
      pageSizeParam = 100,
      assignmentStatusParam = assignmentStatus,
      fromDate = activeFilters.fromDate,
      toDate = activeFilters.toDate,
      testCode = activeFilters.testCode,
      materialName = activeFilters.materialName,
      microorganismName = activeFilters.microorganismName,
      requestId = activeFilters.requestId,
      businessUnitParam = selectedBUFilter,
    } = params || {};

    try {
      setIsLoading(true);
      const effectivePageSize = pageSizeParam ?? pagination?.pageSize ?? 10;

      const response = await api.sendMessage(`admin/lis/infectionAlerts`, {
        user: currentUser(),
        body: {
          page,
          pageSize: effectivePageSize,
          businessUnit: businessUnitParam || undefined,
          assignmentStatus: assignmentStatusParam,
          fromDate: fromDate || undefined,
          toDate: toDate || undefined,
          testCode: testCode || undefined,
          materialName: materialName || undefined,
          microorganismName: microorganismName || undefined,
          requestId: requestId || undefined,
        },
      });

      if (response.success) {
        setNoRightsError(null);
        setTableData(response.data);
        setHeaders(response.headers || []);
        setPagination(response.pagination || null);
        if (response.counters) {
          setCounters(response.counters);
        }
        // Detect admin on first response and load BU list
        if (response.isAdmin && !isAdmin) {
          setIsAdmin(true);
          fetchBusinessUnits();
        }
      } else {
        const errorCode = response.errorCode;
        if (errorCode === 'NO_WARN_RIGHTS' || errorCode === 'NO_BUSINESS_UNIT_ASSIGNMENT') {
          setNoRightsError(response.message || "Non hai i diritti per visualizzare le notifiche LIS.");
          setTableData([]);
        } else {
          MySwal.fire({ icon: "error", title: "Errore", text: response.message || "Errore nel caricamento degli alert" });
        }
      }
    } catch (error) {
      console.error("Error fetching table data:", error);
      MySwal.fire({ icon: "error", title: "Error", text: "Failed to fetch table data" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = () => {
    const filters = {
      fromDate: filterInputs.fromDate,
      toDate: filterInputs.toDate,
      testCode: filterInputs.testCode,
      materialName: filterInputs.materialName,
      microorganismName: filterInputs.microorganismName,
      requestId: filterInputs.requestId,
    };

    setActiveFilters(filters);
    fetchTableData({
      page: 1,
      pageSizeParam: pagination?.pageSize ?? 100,
      ...filters,
    });
  };

  const handleResetFilters = () => {
    const emptyFilters = {
      fromDate: "",
      toDate: "",
      testCode: "",
      materialName: "",
      microorganismName: "",
      requestId: "",
    };

    setFilterInputs(emptyFilters);
    setActiveFilters(emptyFilters);
    fetchTableData({
      page: 1,
      pageSizeParam: pagination?.pageSize ?? 100,
      ...emptyFilters,
    });
  };

  /**
   * Open assignment modal for a specific alert.
   * alertType is derived from the rule flags on the row:
   *   SCHEDA_HAI_ENABLED → 'ICA', SCHEDA_MOALERT_ENABLED → 'ALERT'
   */
  const openAssignModal = async (alert) => {
    const alertType = alert.SCHEDA_HAI_ENABLED ? 'ICA' : 'ALERT';

    try {
      setSelectedAlert(alert);
      setSelectedAlertType(alertType);
      setSelectedUserId("");
      setShowAssignModal(true);

      const response = await api.sendMessage(
        "admin/lis/infectionAlerts/assignableUsers",
        {
          user: currentUser(),
          body: {
            alertType,
            alertBusinessUnitId: alert.Business_Unit_Id,
          },
        }
      );

      if (response.success) {
        // Transform users to add FullName + BusinessUnit for Choices component
        const transformedUsers = (response.users || []).map((user) => ({
          ...user,
          Code: user.Id, // Choices uses "Code" as value
          FullName: user.BusinessUnitName 
            ? `${user.Name} ${user.LastName} (${user.BusinessUnitName})`  // Show BU in dropdown
            : `${user.Name} ${user.LastName}`, // Fallback without BU
          DisplayName: `${user.Name} ${user.LastName}`,  // Name without BU for later use
          BULevel: user.BusinessUnitLevel || 0,  // For sorting
        }));
        
        // Sort by BU Level first, then by name
        transformedUsers.sort((a, b) => {
          if (a.BULevel !== b.BULevel) {
            return a.BULevel - b.BULevel;  // Level 0, 1, 2...
          }
          return a.DisplayName.localeCompare(b.DisplayName);
        });
        
        console.log(
          "✅ Assignable users loaded (sorted by BU hierarchy):",
          transformedUsers.length,
          transformedUsers
        );
        setAssignableUsers(transformedUsers);
      } else {
        throw new Error(response.message || "Failed to fetch assignable users");
      }
    } catch (error) {
      console.error("Error fetching assignable users:", error);
      MySwal.fire({
        icon: "error",
        title: "Errore",
        text: "Impossibile recuperare gli utenti assegnabili",
      });
      closeAssignModal();
    }
  };

  /**
   * Close assignment modal and reset state
   */
  const closeAssignModal = () => {
    setShowAssignModal(false);
    setSelectedAlert(null);
    setSelectedAlertType(null);
    setSelectedUserId("");
    setAssignableUsers([]);
  };

  /**
   * Open details panel for a specific alert
   * Fetches complete test data including all microorganisms and antibiotics
   */
  const openDetailsPanel = async (alert) => {
    try {
      setIsLoadingDetails(true);
      setShowDetailsPanel(true);
      setSelectedAlertDetails(null);

      // Fetch complete test details
      const response = await api.sendMessage(
        "admin/lis/infectionAlerts/details",
        {
          user: currentUser(),
          body: {
            testId: alert.Test_Id,
          },
        }
      );

      if (response.success) {
        setSelectedAlertDetails(response.data);
      } else {
        throw new Error(response.message || "Failed to fetch alert details");
      }
    } catch (error) {
      console.error("Error fetching alert details:", error);
      MySwal.fire({
        icon: "error",
        title: "Errore",
        text: "Impossibile recuperare i dettagli dell'alert",
      });
      closeDetailsPanel();
    } finally {
      setIsLoadingDetails(false);
    }
  };

  /**
   * Close details panel
   */
  const closeDetailsPanel = () => {
    setShowDetailsPanel(false);
    setSelectedAlertDetails(null);
  };

  /**
   * Handle assignment of alert to selected user
   * Shows confirmation dialog before assigning
   */
  const handleAssign = async () => {
    if (!selectedUserId) {
      MySwal.fire({
        icon: "warning",
        title: "Attenzione",
        text: "Seleziona un utente prima di assegnare",
      });
      return;
    }

    // Find selected user name for confirmation message (use Code which is the same as Id)
    const selectedUserObj = assignableUsers.find(
      (u) => u.Code === selectedUserId
    );
    const userName = selectedUserObj
      ? selectedUserObj.DisplayName || selectedUserObj.FullName  // Use DisplayName (without BU) for confirmation
      : "questo utente";

    // Show confirmation dialog
    const confirmResult = await MySwal.fire({
      icon: "question",
      title: "Conferma Assegnazione",
      text: `Sei sicuro di voler assegnare questo alert a ${userName}?`,
      showCancelButton: true,
      confirmButtonText: "Sì, assegna",
      cancelButtonText: "Annulla",
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
    });

    if (!confirmResult.isConfirmed) {
      return;
    }

    try {
      setIsAssigning(true);

      // Show loading modal
      MySwal.fire({
        title: "Assegnazione in corso...",
        text: "Attendere prego",
        allowOutsideClick: false,
        showConfirmButton: false,
        willOpen: () => {
          Swal.showLoading();
        },
      });

      const response = await api.sendMessage(
        "admin/lis/infectionAlerts/assign",
        {
          user: currentUser(),
          body: {
            testId: selectedAlert.Test_Id,
            assignedToUserId: selectedUserId,
            alertType: selectedAlertType,
          },
        }
      );

      if (response.success) {
        // Show success message
        MySwal.fire({
          icon: "success",
          title: "Successo",
          text: "Alert assegnato con successo",
          timer: 2000,
          showConfirmButton: false,
        });

        // Close modal and refresh table
        closeAssignModal();
        fetchTableData({
          page: pagination?.currentPage ?? 1,
          pageSizeParam: pagination?.pageSize ?? 100,
          ...activeFilters,
        });
      } else {
        throw new Error(response.message || "Errore durante l'assegnazione");
      }
    } catch (error) {
      console.error("Error assigning alert:", error);
      MySwal.fire({
        icon: "error",
        title: "Errore",
        text: error.message || "Impossibile assegnare l'alert",
      });
    } finally {
      setIsAssigning(false);
    }
  };

  return (
    <>
      <style>{`
        .lis-alert-drawer .card-header,
        .lis-alert-drawer .card .card-header,
        .lis-alert-drawer .card > .card-header,
        .lis-alert-drawer .sticky-top {
          position: relative !important;
          top: auto !important;
          z-index: auto !important;
          transform: none !important;
          margin-top: 0 !important;
        }
        .lis-alert-drawer .card {
          position: relative !important;
          margin-bottom: 1rem;
          height: auto !important;
          min-height: 0 !important;
          overflow: visible !important;
        }
        .lis-alert-drawer .card-body {
          position: relative !important;
          height: auto !important;
          min-height: 0 !important;
        }
        .lis-alert-drawer,
        .lis-alert-drawer * {
          word-break: normal !important;
          overflow-wrap: break-word !important;
          hyphens: none !important;
        }
        .lis-alert-drawer strong,
        .lis-alert-drawer b,
        .lis-alert-drawer .card-header * {
          display: inline-block !important;
          max-width: 100% !important;
          white-space: normal !important;
          word-break: normal !important;
          overflow-wrap: normal !important;
          hyphens: none !important;
        }
      `}</style>
      
      <div className="page-titles">
        <ol className="breadcrumb"></ol>
      </div>
      <div className="container-fluid">
        <div className="card-body p-0 bg-white">
          <div className="table-responsive active-projects task-table">
            <div className="tbl-caption h-auto ">
              <div className="row">
                <div className="col-12">
                  <div className="d-flex justify-content-between align-items-center">
                    <h3 className="mb-0">
                      <i className="fas fa-address-book me-2"></i>
                      {t("lisAlertAssignment", { ns: "lisLabels" })}
                    </h3>

                    {!noRightsError && <div className="d-flex gap-3 align-items-end">
                      {isAdmin && (
                        <div>
                          <label className="form-label small text-muted mb-1">
                            Unità Operativa
                          </label>
                          <Select
                            options={[
                              { value: "", label: "Tutte le BU" },
                              ...allBusinessUnits.map((bu) => ({ value: bu.Id, label: bu.Name })),
                            ]}
                            value={
                              selectedBUFilter
                                ? allBusinessUnits.find((bu) => bu.Id === selectedBUFilter)
                                    ? { value: selectedBUFilter, label: allBusinessUnits.find((bu) => bu.Id === selectedBUFilter).Name }
                                    : null
                                : { value: "", label: "Tutte le BU" }
                            }
                            onChange={(opt) => {
                              const newBU = opt?.value || "";
                              setSelectedBUFilter(newBU);
                              fetchTableData({ page: 1, pageSizeParam: pagination?.pageSize ?? 100, businessUnitParam: newBU });
                            }}
                            styles={{
                              container: (base) => ({ ...base, minWidth: "220px" }),
                              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            }}
                            classNamePrefix="react-select"
                            placeholder="Seleziona BU..."
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                          />
                        </div>
                      )}
                      <div>
                        <label className="form-label small text-muted mb-1">
                          {t("filterStatus", { ns: "lisLabels" })}
                        </label>
                        <Select
                          options={assignmentStatusOptions}
                          value={assignmentStatusOptions.find(
                            (o) => o.value === assignmentStatus
                          )}
                          onChange={(opt) => {
                            const newStatus = opt?.value;
                            setAssignmentStatus(newStatus);
                            fetchTableData({
                              page: 1,
                              pageSizeParam: pagination?.pageSize ?? 100,
                              assignmentStatusParam: newStatus,
                            });
                          }}
                          styles={{
                            container: (base) => ({ ...base, minWidth: "200px" }),
                          }}
                          classNamePrefix="react-select"
                          isSearchable={false}
                        />
                      </div>
                    </div>}
                  </div>
                  <p className="text-muted">
                    {t("lisAlertAssignmentDescription", { ns: "lisLabels" })}
                  </p>
                </div>
              </div>
              {!noRightsError && <LisTableHeader
                filtersConfig={[
                  {
                    key: "requestId",
                    label: t("requestId", { ns: "lisLabels" }),
                    type: "text",
                    placeholder: t("requestId", { ns: "lisLabels" }),
                  },
                  {
                    key: "testCode",
                    label: t("testCodeLabel", { ns: "lisLabels" }),
                    type: "text",
                    placeholder: t("testCodePlaceholder", { ns: "lisLabels" }),
                  },
                  {
                    key: "materialName",
                    label: t("materialLabel", { ns: "lisLabels" }),
                    type: "text",
                    placeholder: t("materialPlaceholder", { ns: "lisLabels" }),
                  },
                  {
                    key: "microorganismName",
                    label: t("microorganismLabel", { ns: "lisLabels" }),
                    type: "text",
                    placeholder: t("microorganismPlaceholder", { ns: "lisLabels" }),
                  },
                  { key: "fromDate", label: t("fromDateLabel", { ns: "lisLabels" }), type: "date" },
                  { key: "toDate", label: t("toDateLabel", { ns: "lisLabels" }), type: "date" },
                ]}
                values={filterInputs}
                onChange={(key, value) =>
                  setFilterInputs({ ...filterInputs, [key]: value })
                }
                onSearch={handleSearch}
                onReset={handleResetFilters}
                isLoading={isLoading}
                uniformControlStyle={uniformControlStyle}
                columnClass="col-md-2"
              />}
              <div className="card">
                <div className="card-body">
                  {noRightsError ? (
                    <div className="alert alert-warning d-flex align-items-center gap-3 mt-3">
                      <i className="fa-solid fa-lock fa-2x text-warning"></i>
                      <div>
                        <strong>Accesso non consentito</strong>
                        <p className="mb-0 mt-1">{noRightsError}</p>
                      </div>
                    </div>
                  ) : (
                  <LisMainTable
                    headers={headers}
                    rows={tableData}
                    isLoading={isLoading}
                    onRowClick={openDetailsPanel}
                    getRowKey={(row, idx) => row.Test_Id || idx}
                    getRowStyle={(row) => {
                      const isAssigned =
                        row.AssignedTo_Name != null &&
                        row.AssignedTo_Name !== "";
                      return {
                        cursor: "pointer",
                        backgroundColor: isAssigned ? "#f8f9fa" : "inherit",
                      };
                    }}
                    getRowTitle={() => "Clicca per visualizzare dettagli"}
                    noDataMessage="Nessun alert trovato"
                    renderCell={({ row, header, value }) => {
                      if (header.key === "Test_Date") {
                        const days = row.Days_Since_Test;
                        const icon = getDeadlineIcon(days);
                        if (value) {
                          try {
                            const date = new Date(value);
                            const formattedDate = date.toLocaleDateString(
                              "it-IT",
                              { year: "numeric", month: "2-digit", day: "2-digit" }
                            );
                            return (
                              <div className="d-flex align-items-center gap-2">
                                <span>{icon}</span>
                                <div>
                                  <div>{formattedDate}</div>
                                  {days !== null && days !== undefined && (
                                    <small className="text-muted">
                                      {days === 0
                                        ? "Oggi"
                                        : days === 1
                                        ? "1 giorno fa"
                                        : `${days} giorni fa`}
                                    </small>
                                  )}
                                </div>
                              </div>
                            );
                          } catch (e) {
                            // fall through
                          }
                        }
                      }
                      if (header.key === "AssignedOn") {
                        if (value) {
                          try {
                            const date = new Date(value);
                            return date.toLocaleDateString("it-IT", {
                              year: "numeric",
                              month: "2-digit",
                              day: "2-digit",
                              hour: "2-digit",
                              minute: "2-digit",
                            });
                          } catch (e) {
                            // fall through
                          }
                        }
                      }
                      if (header.key === "AssignedTo_Name" && value) {
                        return (
                          <span className="badge bg-success">
                            <i className="fa-solid fa-check me-1"></i>
                            {value}
                          </span>
                        );
                      }
                      if (header.key === "Completion_Status") {
                        const isAssigned = row.AssignedTo_Name != null && row.AssignedTo_Name !== "";
                        if (!isAssigned) return "-";
                        
                        let badgeClass = "bg-light text-dark";
                        let label = "Da iniziare";
                        let icon = "fa-regular fa-circle";

                        const status = value?.toLowerCase();
                        if (status === "saved") {
                          badgeClass = "bg-warning text-dark";
                          label = "In bozza";
                          icon = "fa-solid fa-pen-to-square";
                        } else if (status === "compiled") {
                          badgeClass = "bg-info text-white";
                          label = "Compilata";
                          icon = "fa-solid fa-paper-plane";
                        } else if (status === "approved") {
                          badgeClass = "bg-success text-white";
                          label = "Approvata";
                          icon = "fa-solid fa-check-double";
                        }

                        return (
                          <span className={`badge ${badgeClass}`}>
                            <i className={`${icon} me-1`}></i>
                            {label}
                          </span>
                        );
                      }
                      return value !== undefined && value !== null ? String(value) : "-";
                    }}
                    renderActions={(row) => {
                      const isAssigned =
                        row.AssignedTo_Name != null &&
                        row.AssignedTo_Name !== "";
                      const hasChecklist = row.Completion_Id != null;
                      const schedaEnabled = row.SCHEDA_HAI_ENABLED || row.SCHEDA_MOALERT_ENABLED;
                      const schedaTitle = row.SCHEDA_HAI_ENABLED
                        ? "Assegna scheda ICA"
                        : "Assegna scheda Alert";

                      return (
                        <div className="d-flex gap-1">
                          <button
                            className="btn btn-sm btn-info"
                            onClick={() => openDetailsPanel(row)}
                            title="Visualizza dettagli completi"
                          >
                            <i className="fa-solid fa-eye"></i>
                          </button>
                          {hasChecklist && row.Checklist_Form_Id && (
                            <button
                              className="btn btn-sm btn-success"
                              onClick={() => {
                                const base = `/form/compile/${row.Checklist_Form_Id}/${row.Completion_Id}`;
                                const params = new URLSearchParams({ validation: 'true', readOnly: 'true' });
                                if (row.Checklist_Assignment_Id) params.set('assignmentId', row.Checklist_Assignment_Id);
                                navigate(`${base}?${params.toString()}`);
                              }}
                              title="Apri scheda"
                            >
                              <i className="fa-solid fa-file-signature"></i>
                            </button>
                          )}
                          {!isAssigned && schedaEnabled && (
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => openAssignModal(row)}
                              title={schedaTitle}
                            >
                              <i className="fa-solid fa-user-plus"></i>
                            </button>
                          )}
                        </div>
                      );
                    }}
                    pagination={pagination}
                    onPageChange={(pageNum) =>
                      fetchTableData({
                        page: pageNum,
                        pageSizeParam: pagination?.pageSize,
                        ...activeFilters,
                      })
                    }
                    maxHeight={450}
                  />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Assignment Modal */}
      {showAssignModal && selectedAlert && (
        <div
          id="lis-alert-assign-modal"
          className="modal fade show"
          style={{
            display: "block",
            backgroundColor: "rgba(0,0,0,0.5)",
            zIndex: 1050,
          }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fa-solid fa-user-plus me-2"></i>
                  Assegna Alert
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeAssignModal}
                  disabled={isAssigning}
                ></button>
              </div>

              <div className="modal-body">
                {/* Patient Name */}
                <div className="alert alert-info mb-3">
                  <i className="fa-solid fa-user-injured me-2"></i>
                  <strong>Paziente:</strong> {(selectedAlert.Patient_Name || "").substring(0, 3)}{" "}
                  {(selectedAlert.Patient_LastName || "").substring(0, 3)}
                  <br />
                  <i className="fa-solid fa-bacterium me-2 mt-1"></i>
                  <strong>Microrganismo:</strong>{" "}
                  {selectedAlert.Microorganism_Name || "N/A"}
                </div>

                {/* User Selection Dropdown */}
                <div className="mb-3">
                  <label className="form-label">
                    Seleziona utente a cui assegnare{" "}
                    <span className="text-danger">*</span>
                  </label>
                  <Choices
                    voc={assignableUsers}
                    value="Code"
                    text="FullName"
                    className="inputStyle blackText bg-white"
                    selected={selectedUserId ? [{ Code: selectedUserId }] : []}
                    onChange={(e) => {
                      const selectedCode = e.selected?.[0]?.Code || "";
                      setSelectedUserId(selectedCode);
                    }}
                    disabled={isAssigning}
                    placeholder="Cerca utente..."
                    dropDownParent="root"
                  />
                </div>

                <div className="alert alert-warning small">
                  <i className="fa-solid fa-info-circle me-1"></i>
                  {selectedAlertType === 'ICA'
                    ? "Utenti dell'unità operativa dell'alert con ruolo configurato in Gestione ICA (Scheda = attivo)."
                    : "Utenti appartenenti ai gruppi configurati in Gestione Alert (Scheda = attivo)."}
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeAssignModal}
                  disabled={isAssigning}
                >
                  <i className="fa-solid fa-times me-1"></i>
                  Annulla
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleAssign}
                  disabled={isAssigning || !selectedUserId}
                >
                  <i className="fa-solid fa-check me-1"></i>
                  Assegna
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Details Side Panel */}
      {showDetailsPanel && (
        <>
          {/* Backdrop */}
          <div
            onClick={closeDetailsPanel}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.35)',
              zIndex: 1050,
            }}
          />

          {/* Drawer */}
          <div
            className="lis-alert-drawer"
            style={{
              position: 'fixed',
              top: 0,
              right: 0,
              height: '100vh',
              width: '700px',
              maxWidth: '95vw',
              background: '#fff',
              zIndex: 1051,
              boxShadow: '-8px 0 24px rgba(0,0,0,0.18)',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <style>{`
              .lis-alert-drawer .card-header,
              .lis-alert-drawer .card .card-header,
              .lis-alert-drawer .sticky-top {
                position: relative !important;
                top: auto !important;
                z-index: auto !important;
                transform: none !important;
                margin-top: 0 !important;
              }
              .lis-alert-drawer .card {
                position: relative !important;
                margin-bottom: 1rem;
                height: auto !important;
                min-height: 0 !important;
                overflow: visible !important;
              }
              .lis-alert-drawer .card-body {
                position: relative !important;
                height: auto !important;
                min-height: 0 !important;
              }
              .lis-alert-drawer, .lis-alert-drawer * {
                word-break: normal !important;
                overflow-wrap: break-word !important;
                hyphens: none !important;
              }
            `}</style>
            {/* Drawer Header */}
            <div
              className="border-bottom px-3 py-2 d-flex align-items-center justify-content-between"
              style={{ background: 'var(--bs-body-bg)' }}
            >
              <div className="d-flex flex-column">
                <strong>
                  <i className="fa-solid fa-flask me-2"></i>
                  Dettagli Alert LIS
                </strong>
                {selectedAlertDetails?.test?.code && (
                  <small className="text-muted">
                    Codice esame: {selectedAlertDetails.test.code}
                  </small>
                )}
              </div>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={closeDetailsPanel}
                title="Chiudi"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            {/* Drawer Content */}
            <div
              className="lis-alert-drawer-content"
              style={{ overflowY: 'auto', padding: '1rem', position: 'relative' }}
            >
              {isLoadingDetails ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Caricamento...</span>
                  </div>
                  <p className="text-muted mt-2">Caricamento dettagli...</p>
                </div>
              ) : selectedAlertDetails ? (
                <>
                  {/* Assignment Info */}
                  {selectedAlertDetails.assignment && (
                    <div className="card mb-3">
                      <div
                        className="card-header bg-info text-white d-flex justify-content-between align-items-center"
                        style={{ cursor: 'pointer' }}
                        onClick={() => toggleSection('assignment')}
                      >
                        <h5 className="mb-0 flex-grow-1">
                          <i className="fa-solid fa-user-check me-2"></i>
                          Informazioni Assegnazione
                        </h5>
                        <i className={`fas fa-chevron-${openSections.assignment ? 'up' : 'down'} ms-2`} style={{ fontSize: '1.2rem' }}></i>
                      </div>
                      {openSections.assignment && (
                        <div className="card-body py-2 px-3">
                          <dl className="row mb-0 small" style={{ rowGap: '0.15rem' }}>
                            <dt className="col-5 text-muted fw-normal mb-0">Assegnato a</dt>
                            <dd className="col-7 mb-0 fw-semibold">{selectedAlertDetails.assignment.assignedTo || 'N/A'}</dd>
                            <dt className="col-5 text-muted fw-normal mb-0">Data Assegnazione</dt>
                            <dd className="col-7 mb-0">
                              {selectedAlertDetails.assignment.assignedOn
                                ? new Date(selectedAlertDetails.assignment.assignedOn).toLocaleDateString('it-IT', {
                                    year: 'numeric',
                                    month: '2-digit',
                                    day: '2-digit',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })
                                : 'N/A'}
                            </dd>
                          </dl>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Dati Richiesta */}
                  <div className="card mb-3">
                    <div
                      className="card-header bg-primary text-white d-flex justify-content-between align-items-center"
                      style={{ cursor: 'pointer' }}
                      onClick={() => toggleSection('patient')}
                    >
                      <h5 className="mb-0 flex-grow-1">
                        <i className="fa-solid fa-file-medical me-2"></i>
                        Dati Richiesta
                      </h5>
                      <i className={`fas fa-chevron-${openSections.patient ? 'up' : 'down'} ms-2`} style={{ fontSize: '1.2rem' }}></i>
                    </div>
                    {openSections.patient && (
                      <div className="card-body py-2 px-3">
                        <dl className="row mb-0 small" style={{ rowGap: '0.15rem' }}>
                          <dt className="col-5 text-muted fw-normal mb-0">Nome</dt>
                          <dd className="col-7 mb-0 fw-semibold">{(selectedAlertDetails.patient?.name || '-').substring(0, 3)}</dd>
                          <dt className="col-5 text-muted fw-normal mb-0">Cognome</dt>
                          <dd className="col-7 mb-0 fw-semibold">{(selectedAlertDetails.patient?.lastName || '-').substring(0, 3)}</dd>
                          <dt className="col-5 text-muted fw-normal mb-0">Sesso</dt>
                          <dd className="col-7 mb-0">{selectedAlertDetails.patient?.sex || '-'}</dd>
                          <dt className="col-5 text-muted fw-normal mb-0">Codice Fiscale (parziale)</dt>
                          <dd className="col-7 mb-0">{selectedAlertDetails.patient?.cf || '-'}</dd>
                          <dt className="col-5 text-muted fw-normal mb-0">Data Accettazione</dt>
                          <dd className="col-7 mb-0">
                            {selectedAlertDetails.test?.date
                              ? new Date(selectedAlertDetails.test.date).toLocaleDateString('it-IT')
                              : '-'}
                          </dd>
                          <dt className="col-5 text-muted fw-normal mb-0">Unità Operativa</dt>
                          <dd className="col-7 mb-0">{selectedAlertDetails.businessUnit?.name || '-'}</dd>
                        </dl>
                      </div>
                    )}
                  </div>

                  {/* Colture: struttura identica a microbiological_data_explorer */}
                  <div className="card mb-3">
                    <div
                      className="card-header bg-success text-white d-flex justify-content-between align-items-center"
                      style={{ cursor: 'pointer' }}
                      onClick={() => toggleSection('microorganisms')}
                    >
                      <h5 className="mb-0 flex-grow-1">
                        <i className="fas fa-flask me-2"></i>
                        Colture
                        <span className="badge bg-light text-dark ms-2" style={{ fontSize: '0.75rem' }}>
                          {selectedAlertDetails.materials?.length || 0}
                        </span>
                      </h5>
                      <i className={`fas fa-chevron-${openSections.microorganisms ? 'up' : 'down'} ms-2`} style={{ fontSize: '1.2rem' }}></i>
                    </div>
                    {openSections.microorganisms && (
                      <div className="card-body py-2 px-3">
                        {(selectedAlertDetails.materials?.length > 0) ? (
                          selectedAlertDetails.materials.map((mat, matIdx) => {
                            const normId = (v) => v?.toString()?.toLowerCase()?.replace(/[{}]/g, '') ?? '';
                            const matItems = (selectedAlertDetails.items || []).filter(i => normId(i.Material_Id) === normId(mat.material_id));
                            const cultureKey = mat.material_id || `cult_${matIdx}`;
                            const isCultureCollapsed = !!collapsedMicroorganisms[`cult_${cultureKey}`];
                            return (
                              <div key={matIdx} className={matIdx < selectedAlertDetails.materials.length - 1 ? 'mb-3' : ''}>
                                {/* Strip coltura — verde collassabile */}
                                <div
                                  className="d-flex align-items-center gap-2 px-2 py-1 rounded"
                                  style={{ background: '#f0fdf4', borderLeft: '3px solid #22c55e', cursor: 'pointer' }}
                                  onClick={() => setCollapsedMicroorganisms(prev => ({ ...prev, [`cult_${cultureKey}`]: !prev[`cult_${cultureKey}`] }))}
                                >
                                  <span className="badge bg-success" style={{ fontSize: '0.7rem' }}>Coltura {matIdx + 1}</span>
                                  <span className="fw-semibold small text-success flex-grow-1">
                                    {mat.material_description || mat.Material_Code || '-'}
                                  </span>
                                  {matItems.length > 0 && (
                                    <span className="badge fw-normal me-1" style={{ background: '#dcfce7', color: '#16a34a', border: '1px solid #86efac', fontSize: '0.65rem' }}>
                                      {matItems.length} microrganismi
                                    </span>
                                  )}
                                  <i className={`fas fa-chevron-${isCultureCollapsed ? 'down' : 'up'} text-success`} style={{ fontSize: '0.75rem' }}></i>
                                </div>

                                {!isCultureCollapsed && (
                                  <>
                                    {/* Dettagli materiale — dl.row */}
                                    <dl className="row mb-0 small ps-2 mt-2" style={{ rowGap: '0.15rem' }}>
                                      <dt className="col-5 text-muted fw-normal mb-0">Codice Esame</dt>
                                      <dd className="col-7 mb-0 fw-semibold">{selectedAlertDetails.test?.code || '-'}</dd>
                                      {/* <dt className="col-5 text-muted fw-normal mb-0">Data Esame</dt>
                                      <dd className="col-7 mb-0">
                                        {selectedAlertDetails.test?.date
                                          ? new Date(selectedAlertDetails.test.date).toLocaleDateString('it-IT')
                                          : '-'}
                                      </dd> */}
                                      <dt className="col-5 text-muted fw-normal mb-0">Codice Materiale</dt>
                                      <dd className="col-7 mb-0">{mat.Material_Code || '-'}</dd>
                                      <dt className="col-5 text-muted fw-normal mb-0">Materiale</dt>
                                      <dd className="col-7 mb-0">{mat.material_description || '-'}</dd>
                                      {mat.Provenance_Code && (
                                        <>
                                          <dt className="col-5 text-muted fw-normal mb-0">Codice Provenienza</dt>
                                          <dd className="col-7 mb-0">{mat.Provenance_Code}</dd>
                                        </>
                                      )}
                                      {mat.provenance_description && (
                                        <>
                                          <dt className="col-5 text-muted fw-normal mb-0">Provenienza</dt>
                                          <dd className="col-7 mb-0">{mat.provenance_description}</dd>
                                        </>
                                      )}
                                    </dl>

                                    {/* Microrganismi per questa coltura */}
                                    {matItems.length > 0 && (
                                      <div className="mt-3 ps-2">
                                        <div
                                          className="d-flex align-items-center gap-2 mb-2"
                                          style={{ cursor: 'pointer' }}
                                          onClick={() => setCollapsedMicroorganisms(prev => ({ ...prev, [`microSec_${cultureKey}`]: !prev[`microSec_${cultureKey}`] }))}
                                        >
                                          <h6 className="mb-0 small fw-semibold" style={{ color: '#92600a' }}>
                                            <i className="fas fa-bacteria me-1"></i>
                                            Microrganismi
                                          </h6>
                                          <span className="badge fw-normal" style={{ background: '#fef3c7', color: '#92600a', border: '1px solid #fcd34d', fontSize: '0.65rem' }}>
                                            {matItems.length}
                                          </span>
                                          <i className={`fas fa-chevron-${collapsedMicroorganisms[`microSec_${cultureKey}`] ? 'down' : 'up'} ms-auto`} style={{ fontSize: '0.75rem', color: '#92600a' }}></i>
                                        </div>

                                        {!collapsedMicroorganisms[`microSec_${cultureKey}`] && matItems.map((item, itemIdx) => {
                                          const moKey = item.item_id || `${cultureKey}_${itemIdx}`;
                                          const isMoCollapsed = !!collapsedMicroorganisms[moKey];
                                          const relatedAntibiotics = (selectedAlertDetails.antibiotics || []).filter(
                                            ab => normId(ab.Lis_Item_Id) === normId(item.item_id)
                                          );
                                          const relatedGenes = (selectedAlertDetails.resistanceGenes || []).filter(
                                            g => normId(g.Lis_Item_Id) === normId(item.item_id)
                                          );
                                          return (
                                            <div key={itemIdx} className={itemIdx < matItems.length - 1 ? 'mb-2' : ''}>
                                              {/* Strip microrganismo — giallo collassabile */}
                                              <div
                                                className="d-flex align-items-center gap-2 px-2 py-1 rounded"
                                                style={{ background: '#fffbeb', borderLeft: '3px solid #f59e0b', cursor: 'pointer' }}
                                                onClick={() => setCollapsedMicroorganisms(prev => ({ ...prev, [moKey]: !prev[moKey] }))}
                                              >
                                                <i className="fas fa-bacteria text-warning" style={{ fontSize: '0.75rem' }}></i>
                                                <span className="fw-semibold small flex-grow-1" style={{ color: '#92600a' }}>
                                                  {item.microorganism_description || item.moCode || '-'}
                                                </span>
                                                <span className="text-muted small me-1">{item.moCode}</span>
                                                {relatedAntibiotics.length > 0 && (
                                                  <span className="badge fw-normal" style={{ background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', fontSize: '0.65rem' }}>
                                                    <i className="fas fa-pills me-1"></i>{relatedAntibiotics.length}
                                                  </span>
                                                )}
                                                {relatedGenes.length > 0 && (
                                                  <span className="badge fw-normal" style={{ background: '#f3e8ff', color: '#7c3aed', border: '1px solid #c4b5fd', fontSize: '0.65rem' }}>
                                                    <i className="fas fa-dna me-1"></i>{relatedGenes.length}
                                                  </span>
                                                )}
                                                <i className={`fas fa-chevron-${isMoCollapsed ? 'down' : 'up'}`} style={{ fontSize: '0.75rem', color: '#92600a' }}></i>
                                              </div>

                                              {!isMoCollapsed && (
                                                <div className="ps-3 border-start border-warning pt-2">
                                                  {/* Antibiogramma */}
                                                  {relatedAntibiotics.length > 0 && (
                                                    <div className="mb-2">
                                                      <h6 className="text-primary small mb-1">
                                                        <i className="fas fa-pills me-1"></i>
                                                        Antibiogramma
                                                      </h6>
                                                      <div className="table-responsive" style={{ maxHeight: 280, overflowY: 'auto' }}>
                                                        <table className="table table-sm table-bordered mb-0">
                                                          <thead className="table-light">
                                                            <tr>
                                                              <th style={{ whiteSpace: 'nowrap' }}>Codice ATC</th>
                                                              <th>Antibiotico</th>
                                                              <th style={{ whiteSpace: 'nowrap' }}>MIC</th>
                                                              <th style={{ whiteSpace: 'nowrap' }}>S/R/I</th>
                                                            </tr>
                                                          </thead>
                                                          <tbody>
                                                            {relatedAntibiotics.map((ab, abIdx) => (
                                                              <tr key={abIdx}>
                                                                <td style={{ whiteSpace: 'nowrap', fontFamily: 'monospace' }}>{ab.code || '-'}</td>
                                                                <td>{ab.name || ab.code || '-'}</td>
                                                                <td style={{ whiteSpace: 'nowrap', fontFamily: 'monospace' }}>{ab.mic || ab.MIC || '-'}</td>
                                                                <td>
                                                                  <span className={`badge ${(ab.sensitivity || ab.RSI) === 'S' ? 'bg-success' : (ab.sensitivity || ab.RSI) === 'R' ? 'bg-danger' : (ab.sensitivity || ab.RSI) === 'I' ? 'bg-warning text-dark' : 'bg-secondary'}`}>
                                                                    {ab.sensitivity || ab.RSI || '-'}
                                                                  </span>
                                                                </td>
                                                              </tr>
                                                            ))}
                                                          </tbody>
                                                        </table>
                                                      </div>
                                                    </div>
                                                  )}

                                                  {/* Geni di Resistenza */}
                                                  {relatedGenes.length > 0 && (
                                                    <div>
                                                      <h6 className="small mb-1" style={{ color: '#7c3aed' }}>
                                                        <i className="fas fa-dna me-1"></i>
                                                        Geni di Resistenza
                                                      </h6>
                                                      <div className="table-responsive">
                                                        <table className="table table-sm table-bordered mb-0">
                                                          <thead className="table-light">
                                                            <tr>
                                                              <th style={{ whiteSpace: 'nowrap' }}>Codice Gene</th>
                                                              <th>Descrizione</th>
                                                            </tr>
                                                          </thead>
                                                          <tbody>
                                                            {relatedGenes.map((g, gIdx) => (
                                                              <tr key={gIdx}>
                                                                <td>
                                                                  <span className="badge fw-normal" style={{ background: '#f3e8ff', color: '#7c3aed', border: '1px solid #c4b5fd' }}>
                                                                    {g.geneCode || '-'}
                                                                  </span>
                                                                </td>
                                                                <td>{g.description || <span className="text-muted">—</span>}</td>
                                                              </tr>
                                                            ))}
                                                          </tbody>
                                                        </table>
                                                      </div>
                                                    </div>
                                                  )}

                                                  {relatedAntibiotics.length === 0 && relatedGenes.length === 0 && (
                                                    <small className="text-muted">Nessun dato antibiogramma</small>
                                                  )}
                                                </div>
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            );
                          })
                        ) : (
                          <small className="text-muted">Nessun dato colture disponibile</small>
                        )}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="alert alert-warning">
                  <i className="fa-solid fa-exclamation-triangle me-2"></i>
                  Nessun dettaglio disponibile
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default LisAlertAssignmentScreen;
